import { isPast } from "~/utils/datetime"
import { IncomingOrderStatus } from "~/constants/incomingOrdersStatus"
import { getTailwindValue } from "~/utils/tailwind"
import { matchesStringFilter } from "~/utils/string-filter"

// Fast-path shape — fetched for all active variants on page load. See IncomingOrderDetail for
// the lazy, per-variant detail shape used by dialogs and the overview page.
// `skuItems` is populated only when fetched with status='incoming' (used by the projected-
// fulfillment fallback walk); duplicate skuIds are tolerated since callers sum stock effects.
export type IncomingOrder = {
  source?: "Hakio" | "ERP"
  quantity: number
  status: string
  expectedArrivalDate: Date
  warehouseId: string
  shipmentMethod: string | null
  externalCollectionId: string | null
  // Hakio-managed sent POs carry their collection link here (numeric ids as strings) since they
  // never get an externalCollectionId — that field is ERP-only. NULL for ERP IncomingOrderItem rows.
  collectionIds: string[] | null
  vendorId: string | null
  extraData: Record<string, unknown> | null
  cost?: number | null
  skuItems?: { skuId: string; quantity: number }[] | null
}

export type IncomingOrderDetail = IncomingOrder & {
  productionOrderId: string
  variantId: string
  orderDate: Date
  // Latest ex-factory date across the group's ERP lines; null when the ERP sends none
  expectedDepartureDate: Date | null
  skuInfo: Record<string, number>
  costPrice: number | null // BigQuery value, takes precedence over variant.costPrice
}

// Returned by `sku.getDataForPOs`: detail fields + per-sku skuInfo.
export type POIncomingOrder = IncomingOrder & {
  variantId: string
  productionOrderId: string
  orderDate: Date
  costPrice: number | null
  skuInfo: Record<string, number>
}

export function skuQuantityInOrder(
  order: Pick<IncomingOrderDetail, "skuInfo">,
  skuId: string,
): number {
  return order.skuInfo?.[skuId] ?? 0
}

export function skuCostShareInOrder(
  order: Pick<IncomingOrderDetail, "skuInfo" | "quantity" | "cost">,
  skuId: string,
): number {
  const ratio = order.quantity ? skuQuantityInOrder(order, skuId) / order.quantity : 0
  return (order.cost ?? 0) * ratio
}

/**
 * Orders whose arrival lands strictly after `periodStart` and up to and including `periodEnd`.
 *
 * This mirrors the suggested-order engine's in-period window. The engine selects
 * `weekDiff > arrivalWeek && weekDiff <= nextArrivalWeek` (see `calculateSuggestedOrder`); since
 * `differenceInWeeksFloat(currentWeekStart, date)` is strictly monotonic in `date`, that week-space
 * test is equivalent to this raw-date test against the engine's week-aligned period boundaries
 * (`periodStart = startOfWeek(collectionStart)`, `periodEnd = startOfWeek(collectionEnd) + 7d`).
 *
 * Arrivals at or before `periodStart` are excluded — they're already folded into projected stock
 * at start (`stockLevelAtArrival`) — which prevents double-counting when summing
 * `projectedStockStart + incomingInPeriod`.
 */
export function filterIncomingOrdersInPeriod<T extends { expectedArrivalDate: Date }>(
  orders: T[],
  periodStart: Date,
  periodEnd: Date,
): T[] {
  const startMs = periodStart.getTime()
  const endMs = periodEnd.getTime()
  return orders.filter((o) => {
    const t = o.expectedArrivalDate.getTime()
    return t > startMs && t <= endMs
  })
}

/** Orders landing on or before `periodStart`: the complement of {@link filterIncomingOrdersInPeriod}. */
export function filterIncomingOrdersUpTo<T extends { expectedArrivalDate: Date }>(
  orders: readonly T[],
  periodStart: Date,
): T[] {
  const startMs = periodStart.getTime()
  return orders.filter((o) => o.expectedArrivalDate.getTime() <= startMs)
}

export const calculateIncomingStockStatus = (incomingOrder: IncomingOrder) => {
  if (incomingOrder.status === "arrived") {
    return IncomingOrderStatus.Arrived
  }
  if (incomingOrder.status === "incoming") {
    return isPast(incomingOrder.expectedArrivalDate)
      ? IncomingOrderStatus.Delayed
      : IncomingOrderStatus.OnTime
  }
  return IncomingOrderStatus.OnTime
}

export const incomingOrderStatusColor = (status: IncomingOrderStatus) => {
  if (status === IncomingOrderStatus.Arrived) {
    return getTailwindValue("--color-violet-500")
  }
  if (status === IncomingOrderStatus.OnTime) {
    return getTailwindValue("--color-green-500")
  }
  return getTailwindValue("--color-red-500")
}

export interface ExtraDataFilter {
  field: string // e.g. "lotNo(incoming-order-extradata)"
  operator: string // e.g. "oneOf", "notOneOf", "equals", etc.
  value?: string
  values?: string[]
}

/**
 * Picks the `(incoming-order-extradata)` filters (e.g. PO note, lot no) out of the active
 * product-table filters. Single source of truth for the suffix check so the layout, calendar
 * dialog, and `filterVariants` all agree on what counts as an incoming-order-extradata filter.
 */
export const getIncomingOrderExtraDataFilters = <T extends { field: string }>(filters: T[]): T[] =>
  filters.filter((f) => f.field.includes("(incoming-order-extradata)"))

export interface IncomingOrdersFilterOptions {
  statuses?: IncomingOrderStatus[]
  shipmentMethod?: string | null
  warehouseId?: string | null
  externalCollectionIds?: string[] | null
  // Numeric collection ids the consumer is scoped to. Sent POs match via this; ERP rows match via
  // externalCollectionIds. When both are passed, an order passes if either matches.
  collectionIds?: number[] | null
  collectionPeriod?: { startDate: Date; endDate: Date } | null
  vendorIds?: string[] | null
  extraDataFilters?: ExtraDataFilter[]
}

/**
 * Filters incoming orders based on the provided criteria.
 * Accepts both `IncomingOrder` and `IncomingOrderDetail` arrays.
 */
export function filterIncomingOrders<T extends IncomingOrder>(
  orders: T[],
  options: IncomingOrdersFilterOptions = {},
): T[] {
  const {
    statuses,
    shipmentMethod,
    warehouseId,
    externalCollectionIds,
    collectionIds,
    collectionPeriod,
    vendorIds,
    extraDataFilters,
  } = options

  let filteredOrders = [...orders]

  // Filter by warehouse
  if (warehouseId) {
    filteredOrders = filteredOrders.filter((order) => order.warehouseId === warehouseId)
  }

  // Filter by collection. ERP rows (IncomingOrderItem) carry externalCollectionId; Hakio-managed
  // sent POs carry numeric collectionIds. An order passes when either match. As a fallback for
  // companies with neither linkage, callers can scope by collectionPeriod (arrival date window).
  const hasExternalCollectionFilter = !!externalCollectionIds && externalCollectionIds.length > 0
  const hasCollectionIdsFilter = !!collectionIds && collectionIds.length > 0
  const hasCollectionPeriodFilter = !!collectionPeriod
  if (hasExternalCollectionFilter || hasCollectionIdsFilter || hasCollectionPeriodFilter) {
    const collectionIdStrings =
      hasCollectionIdsFilter && collectionIds ? new Set(collectionIds.map(String)) : null
    const periodStartMs = collectionPeriod ? collectionPeriod.startDate.getTime() : 0
    const periodEndMs = collectionPeriod ? collectionPeriod.endDate.getTime() : 0
    filteredOrders = filteredOrders.filter((x) => {
      const externalMatch =
        hasExternalCollectionFilter &&
        x.externalCollectionId != null &&
        externalCollectionIds.includes(x.externalCollectionId)
      const internalMatch =
        collectionIdStrings != null &&
        x.collectionIds != null &&
        x.collectionIds.some((id) => collectionIdStrings.has(id))
      const arrivalMs = hasCollectionPeriodFilter ? new Date(x.expectedArrivalDate).getTime() : 0
      const periodMatch =
        hasCollectionPeriodFilter && arrivalMs >= periodStartMs && arrivalMs <= periodEndMs
      return externalMatch || internalMatch || periodMatch
    })
  }

  // Filter by vendor
  if (vendorIds && vendorIds.length > 0) {
    filteredOrders = filteredOrders.filter(
      (x) => x.vendorId != null && vendorIds.includes(x.vendorId),
    )
  }

  // Filter by shipment method
  if (shipmentMethod) {
    filteredOrders = filteredOrders.filter(
      (order) => order.shipmentMethod?.toLowerCase() === shipmentMethod.toLowerCase(),
    )
  }

  // Filter by status
  if (statuses && statuses.length > 0) {
    filteredOrders = filteredOrders.filter((order) => {
      return statuses.some((statusType) => {
        if (statusType === IncomingOrderStatus.Arrived) {
          return order.status === "arrived"
        }
        if (statusType === IncomingOrderStatus.OnTime) {
          return order.status === "incoming" && !isPast(new Date(order.expectedArrivalDate))
        }
        if (statusType === IncomingOrderStatus.Delayed) {
          return order.status === "incoming" && isPast(new Date(order.expectedArrivalDate))
        }
        return false
      })
    })
  }

  // Filter by extra data fields
  if (extraDataFilters && extraDataFilters.length > 0) {
    for (const filter of extraDataFilters) {
      const key = filter.field.replace("(incoming-order-extradata)", "")
      filteredOrders = filteredOrders.filter((order) => {
        const orderValue = order.extraData?.[key]
        if (orderValue == null) return false
        return matchesStringFilter(String(orderValue), filter.operator, filter.value, filter.values)
      })
    }
  }

  return filteredOrders
}

/**
 * Gets the available status options for the filter checkboxes
 */
export function getStatusFilterOptions() {
  return [
    {
      value: IncomingOrderStatus.OnTime,
      label: "On Time",
      color: "green" as const,
    },
    {
      value: IncomingOrderStatus.Delayed,
      label: "Delayed",
      color: "red" as const,
    },
    {
      value: IncomingOrderStatus.Arrived,
      label: "Arrived",
      color: "blue" as const,
    },
  ]
}
